import * as Yup from 'yup';
import React, { useMemo } from 'react';
import { useFormik } from 'formik';
import { Trash, WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { Box, ButtonV2, Flex, FormControl, InputV2, TextV2, useToast } from '@withjoy/joykit';
import { VideoFormFields, VideoFormProps } from '../../Video.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Category, DesignLayoutType, ComponentType } from '@graphql/generated';
import { CheckIcon } from '@assets/icons';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { CUSTOM_PAGE_ACTIONS } from '@website-editor/customComponents/CustomComponents.constants';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { RichTextEditor } from '@withjoy/joykit/components/RichTextEditor';
import { LayoutVirtualScrollableArea } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutVirtualScrollableArea';
import { useWebsiteEditorCustomPageTelemetry } from '@website-editor/features/custom-page/WebsiteEditorCustomPage.telemetry';
import { useEventDesignThemingProvider } from '@shared/core/website/theming';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useAddVideoComponentMutation, useUpdateVideoComponentMutation, WebsiteEditorVideoComponentFragment } from '@graphql/generated';
import { BottomActions } from '@website-editor/features/custom-page/components/BottomActions/BottomActions';
import urlParser from 'js-video-url-parser';
import { AspectRatioContainer } from '@shared/components/ApectRatio';
import { VideoHelpDialog } from '../VideoHelpDialog';
import { isVideoUrl } from '@shared/utils/video';

export const VideoForm: React.FC<VideoFormProps> = props => {
  const { containerId, pageSlug, videoData, isEditMode, isSingleComponent } = props;
  const { tq } = useTranslation('websiteEditor');
  const { toast } = useToast();
  const { replaceAndNavigateToScreen } = useNavigationPaneContext();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const telemetry = useWebsiteEditorCustomPageTelemetry();
  const { eventDesign, setCustomComponentBlockData, setShowDeletePopup, setDeleteComponentDetails } = useCustomComponentStateContext<WebsiteEditorVideoComponentFragment>();
  const { theme } = useEventDesignThemingProvider();

  const [addVideoComponent, { loading: addingVideoComponent }] = useAddVideoComponentMutation({
    onCompleted: result => {
      telemetry.websitePageComponentUpdated(pageSlug, 'Add', { action: 'added', id: result.addVideoComponent.id, componentType: ComponentType.video });
      toast(tq`Success! Video has been saved.`, { icon: <CheckIcon /> });
      replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
    },
    onError: () => {
      toast(tq`You have reached the block limit. To add more blocks please create a new page or replace an existing block.`, { icon: <WarningTriangle />, duration: 5000 });
    },
  });
  const [updateVideoComponent, { loading: updatingVideoComponent }] = useUpdateVideoComponentMutation({
    onCompleted: result => {
      telemetry.websitePageComponentUpdated(pageSlug, 'Update', {
        action: 'updated',
        id: result.updateVideoComponent.id,
        componentType: ComponentType.video,
      });
      toast(tq`Success! Video has been saved.`, { icon: <CheckIcon /> });
      setCustomComponentBlockData({} as WebsiteEditorVideoComponentFragment);
      replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
    },
    onError: () => {
      toast(tq`An error occurred while saving your video. Please try again.`, { icon: <WarningTriangle /> });
    },
  });
  const isLoading = addingVideoComponent || updatingVideoComponent;

  const formik = useFormik<VideoFormFields>({
    initialValues: {
      videoUrl: videoData?.videoUrl,
      caption: videoData?.caption,
    },
    validationSchema: Yup.object({
      videoUrl: Yup.string()
        .required(tq`Please enter a valid video URL.`)
        .test('is-video-url', tq`Please enter a valid video URL.`, value => isVideoUrl(value)),
      caption: Yup.string().nullable().notRequired(),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: values => {
      if (isEditMode && videoData?.id) {
        updateVideoComponent({
          variables: { payload: { id: videoData.id, sortOrder: videoData.sortOrder, videoUrl: values.videoUrl ?? '', caption: values.caption ?? '' } },
          awaitRefetchQueries: true,
          refetchQueries: ['PageContainerBySlug'],
        }).then(() => {
          updateWebsitePreview();
        });
      } else {
        addVideoComponent({
          variables: { payload: { containerId, sortOrder: videoData?.sortOrder ?? 0, videoUrl: values.videoUrl ?? '', caption: values.caption ?? '' } },
          awaitRefetchQueries: true,
          refetchQueries: ['PageContainerBySlug'],
        }).then(() => {
          updateWebsitePreview();
        });
      }
    },
  });

  const handleCancel = useEventCallback(() => {
    setCustomComponentBlockData({} as WebsiteEditorVideoComponentFragment);
    replaceAndNavigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
  });

  const handleDeleteClick = useEventCallback(() => {
    if (videoData?.id) {
      setShowDeletePopup(true);
      setDeleteComponentDetails({
        id: videoData.id,
        title: tq`Are you sure you want to delete this video block?`,
        subTitle: tq`The contents of this section will be lost. This action can’t be undone.`,
        deleteCta: tq`Delete Video Block`,
        cancelCta: tq`Cancel`,
        successMessage: tq`Success! Video has been deleted.`,
        errorMessage: tq`An error occurred while deleting your video. Please try again.`,
      });
    }
  });

  const fontMap = useMemo(() => {
    return eventDesign?.fonts?.reduce((acc, font) => ({ ...acc, ...{ [font.category]: font.font?.fontFamily } }), {} as Record<Category, string>);
  }, [eventDesign?.fonts]);

  const embedUrl = useMemo(() => {
    if (!formik.values.videoUrl) {
      return;
    }

    const videoInfo = urlParser.parse(formik.values.videoUrl);

    if (!videoInfo) {
      return;
    }

    return urlParser.create({
      videoInfo,
      format: 'embed',
    });
  }, [formik.values.videoUrl]);

  return (
    <form
      onSubmit={formik.handleSubmit}
      style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
    >
      <LayoutVirtualScrollableArea>
        <Flex
          flexDirection={'column'}
          rowGap={8}
          paddingX={[6, isSingleComponent ? 0 : 5]}
          paddingTop={8}
          paddingBottom={pxToRem(132)}
        >
          <FormControl
            label={
              <Flex
                alignItems="center"
                columnGap={2}
              >
                <TextV2
                  typographyVariant={'label3'}
                  lineHeight={1}
                >
                  {tq`Video URL`}
                </TextV2>
                <VideoHelpDialog />
              </Flex>
            }
            width="100%"
            isInvalid={!!formik.errors.videoUrl}
            error={formik.errors.videoUrl}
          >
            <InputV2
              placeholder="Ex: https://www.youtube.com/watch?v=dQw4w9WgXcQ"
              {...formik.getFieldProps('videoUrl')}
            />
          </FormControl>
          {formik.values.videoUrl && formik.isValid && (
            <>
              <AspectRatioContainer aspectRatio={9 / 5}>
                <Box
                  as={'iframe'}
                  title={''}
                  src={embedUrl}
                  border={'none'}
                  height={'100%'}
                  width={'100%'}
                  overflow={'hidden'}
                  borderRadius={3}
                />
              </AspectRatioContainer>
              <FormControl
                label={tq`Video Caption`}
                width="100%"
              >
                <RichTextEditor
                  content={formik.values.caption}
                  handleChange={(value: string) => {
                    formik.setFieldValue('caption', value);
                  }}
                  toolbarInlineForSelection={false}
                  theme={theme}
                  overrides={{
                    Paragraph: {
                      props: { typographyVariant: eventDesign?.websiteLayout.layoutType === DesignLayoutType.brannan ? 'body4' : 'body3', fontFamily: fontMap?.PARAGRAPH },
                    },
                    Heading: {
                      props: { fontFamily: fontMap?.HEADING },
                    },
                  }}
                />
              </FormControl>
            </>
          )}
          {isEditMode && (
            <ButtonV2
              variant="ghost"
              intent="destructive"
              size="lg"
              startIcon={<Trash />}
              onClick={handleDeleteClick}
            >
              {tq`Delete Video Block`}
            </ButtonV2>
          )}
        </Flex>
      </LayoutVirtualScrollableArea>
      <BottomActions
        onCancel={handleCancel}
        onSave={() => formik.handleSubmit()}
        isLoading={isLoading}
        isSingleComponent={isSingleComponent}
        isSaveDisabled={!formik.isValid}
      />
    </form>
  );
};
